import React from 'react';
import { Layout, Menu, Button } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  TeamOutlined,
  BugOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  KeyOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeStore } from '@stores/themeStore';

const { Sider } = Layout;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { key: 'dashboard', icon: <DashboardOutlined />, label: '仪表盘', path: '/dashboard' },
  { key: 'projects', icon: <ProjectOutlined />, label: '项目', path: '/projects' },
  { key: 'activities', icon: <BellOutlined />, label: '动态', path: '/activities' },
  { key: 'issues', icon: <BugOutlined />, label: '问题', path: '/issues' },
  { key: 'todos', icon: <CarryOutOutlined />, label: '待办', path: '/todos' },
  {
    key: 'reports',
    icon: <FileTextOutlined />,
    label: '报告',
    path: '/reports',
    children: [
      { key: 'daily', icon: <FileTextOutlined />, label: '日报', path: '/reports/daily' },
      { key: 'weekly', icon: <CalendarOutlined />, label: '周报', path: '/reports/weekly' },
      { key: 'biweekly', icon: <CalendarOutlined />, label: '双周报', path: '/reports/biweekly' },
      { key: 'monthly', icon: <CalendarOutlined />, label: '月报', path: '/reports/monthly' },
    ],
  },
  { key: 'team', icon: <TeamOutlined />, label: '团队', path: '/team' },
  { key: 'permissions', icon: <KeyOutlined />, label: '权限管理', path: '/admin/permissions' },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar } = useThemeStore();

  // 获取当前选中的菜单项
  const getSelectedKey = () => {
    const path = location.pathname;
    for (const item of menuItems) {
      if (item.children) {
        const child = item.children.find((c) => path.startsWith(c.path));
        if (child) return child.key;
      }
      if (path.startsWith(item.path)) return item.key;
    }
    return 'dashboard';
  };

  const selectedKey = getSelectedKey();

  const handleMenuClick = ({ key }: { key: string }) => {
    const findPath = (items: MenuItem[]): string | undefined => {
      for (const item of items) {
        if (item.key === key) return item.path;
        if (item.children) {
          const found = findPath(item.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    const path = findPath(menuItems);
    if (path) navigate(path);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={sidebarCollapsed}
      width={220}
      className="fixed left-0 top-0 bottom-0 z-50 shadow-lg"
      style={{ background: '#001529' }}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-gray-700">
        {sidebarCollapsed ? (
          <span className="text-white text-xl font-bold">PMS</span>
        ) : (
          <span className="text-white text-lg font-bold">项目管理系统</span>
        )}
      </div>

      {/* 菜单 */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        onClick={handleMenuClick}
        items={menuItems.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: item.label,
          children: item.children?.map((child) => ({
            key: child.key,
            icon: child.icon,
            label: child.label,
          })),
        }))}
        className="border-r-0"
      />

      {/* 折叠按钮 */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
        <Button
          type="text"
          icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggleSidebar}
          className="text-gray-400 hover:text-white"
          block
        >
          {!sidebarCollapsed && '收起菜单'}
        </Button>
      </div>
    </Sider>
  );
};
